import React, { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Sparkles, Shield } from 'lucide-react';
import { useAddToChat } from '../../../../components/chat/chatContext';
import { formatGameTime } from '../utils/gameHelpers';
import { isGameLive, isGameFinal } from '../rtdb/rtdbConfig';
import PredictButton from "../../../../components/dashboards/components/shared/PredictButton";
import XPicksButton from "../../../../components/dashboards/components/shared/XPicksButton";
import ViewOddsButton from "../../../../components/dashboards/components/shared/ViewOddsButton";
import PredictionBox from "../../../../components/dashboards/components/shared/PredictionBox";

const NHLDesktopGameCard = memo(({
  game,
  isSelected,
  isFrozen = false,
  onToggleSelection,
  prediction,
  onGameSelect,
  onViewOdds
}) => {
  const navigate = useNavigate();
  const handleAddToChat = useAddToChat();

  // Handler to stage prediction and navigate to chat
  const handleAnalyzeInChat = async () => {
    if (!prediction) return;

    const message = `Analyze this NHL prediction:

${teams?.away?.name} @ ${teams?.home?.name}

Prediction: ${prediction.winner}${prediction.confidenceNumeric ? ` (${prediction.confidenceNumeric}% confidence)` : ''}
Confidence Level: ${prediction.confidence}
${prediction.reason ? `Reasoning: ${prediction.reason}` : ''}

Can you provide a deeper analysis of this matchup?`;

    await handleAddToChat(message);
    navigate('/chat');
  };

  if (!game) return null;

  const { teams, scores, status, date, time, league } = game;
  const effectiveFrozen = isFrozen || Boolean(prediction);

  // Check game status
  const statusShort = status?.short;
  const isLive = isGameLive(statusShort);
  const isFinished = isGameFinal(statusShort);
  const isUpcoming = !isLive && !isFinished;

  // Format game time
  const formatTime = () => {
    if (isLive) return 'LIVE';
    if (isFinished) return 'Final';
    return formatGameTime(date, time) || 'TBD';
  };

  // Get period display for live games
  const getPeriodDisplay = () => {
    if (!isLive) return null;

    // NHL periods: P1, P2, P3, OT
    if (['P1', 'P2', 'P3', 'OT', 'PT'].includes(statusShort)) {
      return statusShort;
    }

    return 'In Progress';
  };

  const cardClasses = `relative border ${
    isSelected ? 'border-white/60 shadow-[0_0_20px_rgba(255,255,255,0.05)]' : 'border-white/10 hover:border-white/40'
  } rounded-sm p-5 bg-white/[0.03] transition-all duration-200`;

  return (
    <div className={cardClasses}>
      {/* Status Badge - Top Right */}
      {(isLive || isFinished) && (
        <div className="absolute top-3 right-3 z-10">
          <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full ${
            isLive ? 'bg-red-500 animate-pulse' : 'bg-gray-700'
          }`}>
            {isLive && <div className="w-1.5 h-1.5 bg-white rounded-full animate-ping" />}
            <span className="text-[10px] font-black text-white uppercase">
              {isLive ? getPeriodDisplay() : 'FINAL'}
            </span>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 mb-3 text-[11px] font-mono uppercase tracking-widest text-gray-500">
        <Shield className="w-3.5 h-3.5 text-white/60" />
        <span>{league?.name || 'NHL'}</span>
      </div>

      {/* Teams and Scores */}
      <div className="space-y-2.5">
        {/* Away Team */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1">
            {teams?.away?.logo && (
              <img
                src={teams.away.logo}
                alt={teams.away.name}
                className="w-10 h-10 object-contain border border-white/10 rounded-sm bg-black/40"
                loading="lazy"
              />
            )}
            <span className="text-sm font-mono text-white tracking-tight">
              {teams?.away?.name || 'Away Team'}
            </span>
          </div>
          <span className="text-2xl font-black text-white tabular-nums">
            {isLive || isFinished ? (scores?.away ?? '-') : '-'}
          </span>
        </div>

        {/* Home Team */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1">
            {teams?.home?.logo && (
              <img
                src={teams.home.logo}
                alt={teams.home.name}
                className="w-10 h-10 object-contain border border-white/10 rounded-sm bg-black/40"
                loading="lazy"
              />
            )}
            <span className="text-sm font-mono text-white tracking-tight">
              {teams?.home?.name || 'Home Team'}
            </span>
          </div>
          <span className="text-2xl font-black text-white tabular-nums">
            {isLive || isFinished ? (scores?.home ?? '-') : '-'}
          </span>
        </div>
      </div>

      {/* Game Time */}
      {isUpcoming && (
        <div className="mt-4 flex items-center justify-between text-[11px] font-mono uppercase tracking-widest text-gray-400 border-t border-white/5 pt-3">
          <div className="flex items-center gap-2">
            <Clock size={12} />
            <span>{formatTime()}</span>
          </div>
          {game?.arena?.name && (
            <span className="text-ellipsis overflow-hidden whitespace-nowrap max-w-[60%] text-right">
              {game.arena.name}
            </span>
          )}
        </div>
      )}

      {prediction && (
        <div className="pt-2">
          <PredictionBox
            prediction={prediction}
            onAnalyze={handleAnalyzeInChat}
            variant="desktop"
          />
        </div>
      )}

      <div className="mt-3 grid grid-cols-3 gap-2 items-stretch">
        {/* xPicks Button */}
        {(isUpcoming || isLive) && (
          <div className="col-span-1">
            <XPicksButton
              gameData={{
                homeTeam: teams?.home?.name,
                awayTeam: teams?.away?.name,
                homeTeamId: teams?.home?.id,
                awayTeamId: teams?.away?.id,
                leagueId: league?.id || 57, // NHL league ID (57 = primary NHL)
                date: date,
                time: formatTime(),
                gameId: game.id
              }}
              sport="nhl"
              label="XPICKS"
            />
          </div>
        )}

        {/* Prediction Selection Button */}
        {(isUpcoming || isLive) && onToggleSelection && (
          <div className="col-span-1">
            <PredictButton
              label={effectiveFrozen ? 'COOLDOWN' : isSelected ? 'SELECTED' : isLive ? 'PREDICT LIVE' : 'PREDICT'}
              isSelected={isSelected}
              isLive={isLive}
              isFrozen={effectiveFrozen}
              onClick={(e) => {
                e.stopPropagation();
                if (!effectiveFrozen) {
                  onToggleSelection(game);
                }
              }}
            />
          </div>
        )}

        {/* View Odds Button */}
        {/* Disabled until integration with the API to show odds
        {onViewOdds && (
          <div className="col-span-1">
            <ViewOddsButton
              label="VIEW ODDS"
              icon={Eye}
              onClick={(e) => {
                e.stopPropagation();
                onViewOdds(game);
              }}
            />
          </div>
        )}
        */}
      </div>
    </div>
  );
});

NHLDesktopGameCard.displayName = 'NHLGameCardWithPrediction';

export default NHLDesktopGameCard;
